import { useState } from 'react';
import { CheckCircle, Clock, ExternalLink, Loader2, Undo2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BaseSheet } from '@/components/common/BaseSheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BulkUpdateService } from '@/services/agent/bulk/BulkUpdateService';
import type {
  BulkUpdateJobItemStatus,
  BulkUpdateJobItemWithAgent,
  BulkUpdateJobSummary,
} from '@/types/agent/bulk-update';

const ITEMS_PAGE_SIZE = 50;
const LOG_WINDOW_MS = 2 * 60 * 1000;
const SUPABASE_PROJECT_REF = new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0];

const STATUS_TABS: { value: BulkUpdateJobItemStatus; label: string }[] = [
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
];

interface BulkUpdateJobItemsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  job: BulkUpdateJobSummary | null;
}

const getDefaultStatus = (job: BulkUpdateJobSummary | null): BulkUpdateJobItemStatus => {
  if (job?.failed) return 'failed';
  return job?.pending ? 'pending' : 'completed';
};

// Items that failed before failed_at existed only have the time their job started.
const getItemLogsUrl = (item: BulkUpdateJobItemWithAgent) => {
  const time = new Date(item.failed_at ?? item.created_at).getTime();
  const start = new Date(time - LOG_WINDOW_MS).toISOString();
  const end = new Date(time + LOG_WINDOW_MS).toISOString();
  return `https://supabase.com/dashboard/project/${SUPABASE_PROJECT_REF}/functions/handle-agent/logs?its=${start}&ite=${end}`;
};

export const BulkUpdateJobItemsSidebar = ({ isOpen, onClose, job }: BulkUpdateJobItemsSidebarProps) => {
  const [status, setStatus] = useState<BulkUpdateJobItemStatus>(() => getDefaultStatus(job));
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['bulk-update-job-items', job?.id, status, page],
    queryFn: () =>
      BulkUpdateService.getJobItemsWithAgentNames(job!.id, status, ITEMS_PAGE_SIZE, (page - 1) * ITEMS_PAGE_SIZE),
    enabled: isOpen && !!job,
  });

  const firstItemUpdates = data?.data[0]?.updates as { source?: string } | null | undefined;
  const isDefaultPronunciationsJob = firstItemUpdates?.source === 'default-pronunciations';
  const isSettled = !!job && job.status !== 'processing' && job.status !== 'pending';

  const { data: restorableCount = 0 } = useQuery({
    queryKey: ['bulk-update-job-items', job?.id, 'restorable'],
    queryFn: () => BulkUpdateService.countRestorableItems(job!.id),
    enabled: isOpen && isSettled,
  });

  const restore = useRestoreJob(job);

  const handleStatusChange = (value: string) => {
    setStatus(value as BulkUpdateJobItemStatus);
    setPage(1);
  };

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={`Bulk Update Job #${job?.id ?? ''}`}
      description={job ? `${job.total} genies, started ${new Date(job.created_at).toLocaleString()}` : undefined}
    >
      <div className="space-y-4">
        {job && <JobStateNotice job={job} />}
        {job && isDefaultPronunciationsJob && job.status !== 'processing' && <RepairPronunciations job={job} />}
        {restorableCount > 0 && (
          <RestoreSettings
            genieCount={restorableCount}
            isPending={restore.isPending}
            onConfirm={(onDone) => restore.mutate(undefined, { onSuccess: onDone })}
          />
        )}

        <Tabs value={status} onValueChange={handleStatusChange}>
          <TabsList className="grid w-full grid-cols-3">
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="min-h-[44px]">
                {tab.label} ({job?.[tab.value] ?? 0})
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="space-y-2">
          {isLoading ? (
            <div className="text-center py-8 text-genie-text-secondary">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
              Loading job items...
            </div>
          ) : isError ? (
            <div className="text-center py-8 text-red-600 text-sm">Could not load the items for this job</div>
          ) : data?.data.length === 0 ? (
            <div className="text-center py-8 text-genie-text-muted">No {status} items in this job</div>
          ) : (
            data?.data.map((item) => (
              <BulkUpdateJobItemRow
                key={item.id}
                item={item}
                canUndo={isSettled && item.status === 'completed' && !!item.applied_hash && !item.restored_at}
                isUndoing={restore.isPending && restore.variables === item.agent_id}
                onUndo={() => item.agent_id && restore.mutate(item.agent_id)}
              />
            ))
          )}
        </div>

        {data && data.count > ITEMS_PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 text-sm text-genie-text-secondary">
            <span>
              {(page - 1) * ITEMS_PAGE_SIZE + 1} to {Math.min(page * ITEMS_PAGE_SIZE, data.count)} of {data.count}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" className="min-h-[44px]" onClick={() => setPage(page - 1)} disabled={page === 1}>
                Previous
              </Button>
              <Button
                variant="outline"
                className="min-h-[44px]"
                onClick={() => setPage(page + 1)}
                disabled={page * ITEMS_PAGE_SIZE >= data.count}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </BaseSheet>
  );
};

const JobStateNotice = ({ job }: { job: BulkUpdateJobSummary }) => {
  if (!job.pending || (job.status !== 'failed' && job.status !== 'processing')) return null;

  const stopped = job.status === 'failed';

  return (
    <div
      className={
        stopped
          ? 'rounded-lg border border-genie-error/20 bg-genie-error-light p-3 text-sm text-gray-700'
          : 'rounded-lg border border-genie-primary/20 bg-genie-primary-light p-3 text-sm text-gray-700'
      }
    >
      <p className="font-semibold text-gray-900">{stopped ? 'This job stopped early' : 'This job is processing'}</p>
      <p className="mt-1">
        {stopped && `${job.pending} genies were never processed. `}
        {!stopped && job.kind && 'It continues automatically every minute. '}
        {!stopped && !job.kind && `If the pending count stays at ${job.pending} across refreshes, the job has stopped. `}
        {!job.last_error && `To find the cause of a problem, search the handle-agent function logs in Supabase for "Job ${job.id}".`}
      </p>
      {job.last_error && <p className="mt-1 text-red-600 whitespace-pre-wrap break-words">{job.last_error}</p>}
    </div>
  );
};

const RepairPronunciations = ({ job }: { job: BulkUpdateJobSummary }) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const queryClient = useQueryClient();

  const repair = useMutation({
    mutationFn: () => BulkUpdateService.repairDefaultPronunciations(job.id),
    onSuccess: ({ jobId, genieCount }) => {
      toast.success(`Repair job #${jobId} will start within a minute for ${genieCount} genies`);
      queryClient.invalidateQueries({ queryKey: ['bulk-update-jobs'] });
      setIsConfirming(false);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const genieCount = job.completed + job.failed;

  return (
    <div className="rounded-lg border border-genie-border p-3 text-sm text-gray-700 space-y-2">
      <p>Rebuild the pronunciation dictionary for the {genieCount} genies this job processed, even if their words are already saved.</p>
      {isConfirming ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="min-h-[44px]" onClick={() => setIsConfirming(false)} disabled={repair.isPending}>
            Cancel
          </Button>
          <Button
            className="min-h-[44px] bg-gradient-to-r from-genie-primary to-genie-secondary text-white"
            onClick={() => repair.mutate()}
            disabled={repair.isPending}
          >
            {repair.isPending ? 'Starting...' : `Update ${genieCount} live genies`}
          </Button>
        </div>
      ) : (
        <Button variant="outline" className="min-h-[44px]" onClick={() => setIsConfirming(true)}>
          Repair pronunciations
        </Button>
      )}
    </div>
  );
};

// With an agent id it undoes that genie alone, otherwise every genie the job changed.
const useRestoreJob = (job: BulkUpdateJobSummary | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId?: string) => BulkUpdateService.restoreJob(job!.id, agentId ? [agentId] : undefined),
    onSuccess: ({ jobId, genieCount }) => {
      toast.success(`Undo job #${jobId} will start within a minute for ${genieCount} ${genieCount === 1 ? 'genie' : 'genies'}`);
      queryClient.invalidateQueries({ queryKey: ['bulk-update-jobs'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

interface RestoreSettingsProps {
  genieCount: number;
  isPending: boolean;
  onConfirm: (onDone: () => void) => void;
}

const RestoreSettings = ({ genieCount, isPending, onConfirm }: RestoreSettingsProps) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const genies = `${genieCount} ${genieCount === 1 ? 'genie' : 'genies'}`;

  return (
    <div className="rounded-lg border border-genie-border p-3 text-sm text-gray-700 space-y-2">
      <p>
        Put back the settings {genies} had before this job. A genie edited since the job ran is left as it is and listed
        under Failed in the undo job.
      </p>
      {isConfirming ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="min-h-[44px]" onClick={() => setIsConfirming(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="min-h-[44px] bg-gradient-to-r from-genie-primary to-genie-secondary text-white"
            onClick={() => onConfirm(() => setIsConfirming(false))}
            disabled={isPending}
          >
            {isPending ? 'Starting...' : `Restore ${genies}`}
          </Button>
        </div>
      ) : (
        <Button variant="outline" className="min-h-[44px]" onClick={() => setIsConfirming(true)}>
          <Undo2 className="h-4 w-4 mr-2" />
          Undo this job
        </Button>
      )}
    </div>
  );
};

const UndoGenieButton = ({ isUndoing, onUndo }: { isUndoing: boolean; onUndo: () => void }) => {
  const [isConfirming, setIsConfirming] = useState(false);

  if (!isConfirming) {
    return (
      <Button variant="outline" size="sm" className="min-h-[44px] mt-1" onClick={() => setIsConfirming(true)}>
        <Undo2 className="h-3 w-3 mr-1" />
        Undo for this genie
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 mt-1">
      <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => setIsConfirming(false)} disabled={isUndoing}>
        Cancel
      </Button>
      <Button
        size="sm"
        className="min-h-[44px] bg-gradient-to-r from-genie-primary to-genie-secondary text-white"
        onClick={onUndo}
        disabled={isUndoing}
      >
        {isUndoing ? 'Starting...' : 'Restore previous settings'}
      </Button>
    </div>
  );
};

interface BulkUpdateJobItemRowProps {
  item: BulkUpdateJobItemWithAgent;
  canUndo: boolean;
  isUndoing: boolean;
  onUndo: () => void;
}

const BulkUpdateJobItemRow = ({ item, canUndo, isUndoing, onUndo }: BulkUpdateJobItemRowProps) => (
  <div className="flex items-start gap-2 p-3 bg-genie-background rounded text-sm">
    {item.status === 'completed' && <CheckCircle className="h-4 w-4 text-genie-primary flex-shrink-0 mt-0.5" />}
    {item.status === 'failed' && <XCircle className="h-4 w-4 text-genie-error flex-shrink-0 mt-0.5" />}
    {item.status !== 'completed' && item.status !== 'failed' && (
      <Clock className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
    )}

    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-gray-700 font-medium truncate">{item.agent_name || item.agent_id}</p>
        {item.agent_id && (
          <Link
            to={`/genie-hub/my-genies/${item.agent_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-genie-primary hover:underline inline-flex items-center gap-1 text-xs flex-shrink-0"
          >
            View Genie
            <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>
      {item.agent_name && <p className="text-xs text-gray-500 truncate">{item.agent_id}</p>}
      {item.error && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{item.error}</p>}
      {item.restored_at && (
        <p className="text-xs text-genie-text-secondary">Settings restored {new Date(item.restored_at).toLocaleString()}</p>
      )}
      {canUndo && <UndoGenieButton isUndoing={isUndoing} onUndo={onUndo} />}
      {item.status === 'failed' && (
        <Button asChild variant="outline" size="sm" className="min-h-[44px] mt-1">
          <a href={getItemLogsUrl(item)} target="_blank" rel="noopener noreferrer">
            {item.failed_at ? 'View logs' : 'View logs from job start'}
            <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </Button>
      )}
    </div>
  </div>
);
